import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { DietProfile, DietPlan } from '../types/diet';
import { Send, Loader2, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { BodyTypeSelector } from './BodyTypeSelector';
import { FileUpload } from './FileUpload';
import { bodyTypes } from '../types/diet';
import { sendDietPlanWebhook } from '../lib/webhook';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';

interface DietAnalysisProps {
  userId: string;
}

export function DietAnalysis({ userId }: DietAnalysisProps) {
  const [profile, setProfile] = useState<DietProfile | null>(null);
  const [currentPlan, setCurrentPlan] = useState<DietPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [bodyImage, setBodyImage] = useState<File | null>(null);
  const [bloodResultsImage, setBloodResultsImage] = useState<File | null>(null);
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [desiredBodyType, setDesiredBodyType] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekDays, setWeekDays] = useState<Date[]>([]);
  const [weekPlans, setWeekPlans] = useState<Record<string, DietPlan>>({});
  const [fetchingPlans, setFetchingPlans] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchDietProfile();
      generateWeekDays();
    }
  }, [userId]);

  useEffect(() => {
    if (profile?.id) {
      fetchWeekPlans();
    }
  }, [profile, weekDays]);

  useEffect(() => {
    if (weekPlans) {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      setCurrentPlan(weekPlans[formattedDate] || null);
    }
  }, [selectedDate, weekPlans]);

  const generateWeekDays = () => {
    const start = startOfWeek(new Date(), { weekStartsOn: 1 }); // Start from Monday
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    setWeekDays(days);
  };

  const getDayName = (date: Date) => {
    return format(date, 'EEEE');
  };

  const fetchDietProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('diet_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setProfile(data);
        setDesiredBodyType(data.desired_body_type);
        setGender(data.desired_body_type?.includes('female') ? 'female' : 'male');
      }
    } catch (error: any) {
      console.error('Error fetching diet profile:', error);
      setError(error.message);
    }
  };

  const fetchWeekPlans = async () => {
    if (!profile?.id || fetchingPlans) return;

    try {
      setFetchingPlans(true);
      setError(null);

      const weekDates = weekDays.map(date => format(date, 'yyyy-MM-dd'));
      
      const { data: plans, error: plansError } = await supabase
        .from('diet_plans')
        .select('*')
        .eq('diet_profile_id', profile.id)
        .in('start_date', weekDates);

      if (plansError) throw plansError;

      // Create a map of date to plan
      const planMap: Record<string, DietPlan> = {};
      if (Array.isArray(plans)) {
        plans.forEach(plan => {
          if (plan && plan.start_date) {
            planMap[plan.start_date] = plan;
          }
        });
      }

      setWeekPlans(planMap);
    } catch (error: any) {
      console.error('Error fetching week plans:', error);
      setError(`Error fetching plans: ${error.message}`);
    } finally {
      setFetchingPlans(false);
    }
  };

  const handleImageUpload = async (file: File, type: 'body' | 'blood') => {
    if (!file) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}_${userId}_${Math.random()}.${fileExt}`;
      const filePath = `${type}_images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('diet_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('diet_images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error: any) {
      console.error(`Error uploading ${type} image:`, error);
      setError(`Failed to upload ${type} image: ${error.message}`);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      let bodyImageUrl = null;
      let bloodResultsUrl = null;

      if (bodyImage) {
        bodyImageUrl = await handleImageUpload(bodyImage, 'body');
      }

      if (bloodResultsImage) {
        bloodResultsUrl = await handleImageUpload(bloodResultsImage, 'blood');
      }

      const profileData = {
        user_id: userId,
        weight: parseFloat(formData.get('weight') as string),
        height: parseInt(formData.get('height') as string),
        waist: parseInt(formData.get('waist') as string),
        chest: parseInt(formData.get('chest') as string),
        hips: parseInt(formData.get('hips') as string),
        activity_level: formData.get('activity_level'),
        current_diet: formData.get('current_diet'),
        allergies: formData.get('allergies'),
        goal: formData.get('goal'),
        target_areas: (formData.get('target_areas') as string).split(',').map(area => area.trim()),
        meals_per_day: parseInt(formData.get('meals_per_day') as string),
        favorite_foods: (formData.get('favorite_foods') as string).split(',').map(food => food.trim()),
        blood_results: formData.get('blood_results'),
        desired_body_type: desiredBodyType,
        ...(bodyImageUrl && { body_image: bodyImageUrl }),
        ...(bloodResultsUrl && { blood_results_image: bloodResultsUrl })
      };

      const { data, error } = profile?.id
        ? await supabase
            .from('diet_profiles')
            .update(profileData)
            .eq('id', profile.id)
            .select()
            .single()
        : await supabase
            .from('diet_profiles')
            .insert([profileData])
            .select()
            .single();

      if (error) throw error;

      setProfile(data);
      await requestDietPlan(data.id);
    } catch (error: any) {
      console.error('Error saving diet profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const requestDietPlan = async (profileId: string) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('diet_profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (profileError) throw profileError;
      if (!profileData) throw new Error('Profile not found');

      // Delete existing plans for the week
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekDates = weekDays.map(date => format(date, 'yyyy-MM-dd'));

      const { error: deleteError } = await supabase
        .from('diet_plans')
        .delete()
        .eq('diet_profile_id', profileId)
        .in('start_date', weekDates);

      if (deleteError) throw deleteError;

      const webhookResponse = await sendDietPlanWebhook({
        profile_id: profileId,
        weight: profileData.weight,
        height: profileData.height,
        waist: profileData.waist,
        chest: profileData.chest,
        hips: profileData.hips,
        activity_level: profileData.activity_level,
        current_diet: profileData.current_diet,
        allergies: profileData.allergies,
        goal: profileData.goal,
        target_areas: profileData.target_areas,
        meals_per_day: profileData.meals_per_day,
        favorite_foods: profileData.favorite_foods,
        desired_body_type: profileData.desired_body_type
      });

      // Insert plans for each day of the week
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
      const newPlans = [];

      for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const date = format(addDays(weekStart, i), 'yyyy-MM-dd');
        
        if (webhookResponse[day]?.plan) {
          newPlans.push({
            diet_profile_id: profileId,
            start_date: date,
            end_date: date,
            meals: webhookResponse[day]!.plan.meals,
            recommendations: webhookResponse[day]!.plan.recommendations,
            supplements: webhookResponse[day]!.plan.supplements
          });
        }
      }

      if (newPlans.length > 0) {
        const { error: insertError } = await supabase
          .from('diet_plans')
          .insert(newPlans);

        if (insertError) throw insertError;
      }

      await fetchWeekPlans();
      return newPlans[0]; // Return first day's plan
    } catch (error: any) {
      console.error('Error generating diet plan:', error);
      setError(error.message);
      throw error;
    }
  };

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl">
          {error}
        </div>
      )}
      
      {profile && (
        <div className="bg-white p-8 rounded-[30px] shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-700">Your Diet Plan</h2>
            <button
              onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 px-4 py-2 bg-custom-orange-50 text-custom-orange-500 rounded-xl hover:bg-custom-orange-100 transition-all"
            >
              {showForm ? (
                <>
                  Hide Form
                  <ChevronUp size={20} />
                </>
              ) : (
                <>
                  Update Plan
                  <ChevronDown size={20} />
                </>
              )}
            </button>
          </div>

          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {weekDays.map((day) => (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDate(day)}
                className={`px-6 py-3 rounded-xl whitespace-nowrap transition-all ${
                  isSameDay(selectedDate, day)
                    ? 'bg-custom-orange-400 text-white'
                    : weekPlans[format(day, 'yyyy-MM-dd')]
                    ? 'bg-custom-orange-50 text-custom-orange-500 hover:bg-custom-orange-100'
                    : 'bg-gray-50 text-gray-400'
                }`}
              >
                <div className="font-medium">{getDayName(day)}</div>
                <div className="text-sm opacity-80">{format(day, 'MMM d')}</div>
              </button>
            ))}
          </div>

          {currentPlan ? (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentPlan.meals.map((meal, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-custom-orange-50 to-custom-orange-100 p-6 rounded-xl"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-custom-orange-600">{meal.name}</h3>
                      <span className="text-sm text-custom-orange-500">{meal.time}</span>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {meal.foods.map((food, foodIndex) => (
                        <li key={foodIndex} className="text-gray-700">{food}</li>
                      ))}
                    </ul>
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="text-center">
                        <div className="font-medium text-custom-orange-500">{meal.calories}</div>
                        <div className="text-gray-500">kcal</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-custom-orange-500">{meal.protein}g</div>
                        <div className="text-gray-500">Protein</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-custom-orange-500">{meal.carbs}g</div>
                        <div className="text-gray-500">Carbs</div>
                      </div>
                      <div className="text-center">
                        <div className="font-medium text-custom-orange-500">{meal.fats}g</div>
                        <div className="text-gray-500">Fats</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                  <h3 className="font-semibold text-green-600 mb-4">Recommendations</h3>
                  <ul className="space-y-2">
                    {currentPlan.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-green-400 mt-2" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                  <h3 className="font-semibold text-purple-600 mb-4">Supplements</h3>
                  <ul className="space-y-2">
                    {currentPlan.supplements.map((supplement, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <div className="w-2 h-2 rounded-full bg-purple-400 mt-2" />
                        {supplement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No diet plan for this day</p>
              <button
                onClick={() => requestDietPlan(profile.id)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-custom-orange-400 to-custom-orange-500 text-white rounded-xl font-semibold hover:from-custom-orange-500 hover:to-custom-orange-600 transition-all shadow-lg"
              >
                <Plus size={20} />
                Create Plan for {format(selectedDate, 'MMMM d')}
              </button>
            </div>
          )}
        </div>
      )}

      {!profile && (
        <div className="bg-white p-8 rounded-[30px] shadow-xl text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">No Diet Profile Yet</h2>
          <p className="text-gray-600 mb-6">Create your diet profile to get started on your journey.</p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-custom-orange-400 to-custom-orange-500 text-white rounded-xl font-semibold hover:from-custom-orange-500 hover:to-custom-orange-600 transition-all shadow-lg"
          >
            <Plus size={20} />
            Create Diet Profile
          </button>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-8 rounded-[30px] shadow-xl">
          <h2 className="text-2xl font-bold text-gray-700 mb-6">
            {profile ? 'Update Diet Profile' : 'Create Diet Profile'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">Desired Body Type</h3>
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => setGender('male')}
                  className={`px-6 py-2 rounded-xl font-medium transition-all ${
                    gender === 'male'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Male
                </button>
                <button
                  type="button"
                  onClick={() => setGender('female')}
                  className={`px-6 py-2 rounded-xl font-medium transition-all ${
                    gender === 'female'
                      ? 'bg-pink-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Female
                </button>
              </div>
              
              <BodyTypeSelector
                bodyTypes={bodyTypes}
                selectedType={desiredBodyType}
                onSelect={setDesiredBodyType}
                gender={gender}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      name="weight"
                      step="0.1"
                      defaultValue={profile?.weight}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      name="height"
                      defaultValue={profile?.height}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Waist (cm)
                    </label>
                    <input
                      type="number"
                      name="waist"
                      defaultValue={profile?.waist}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Chest (cm)
                    </label>
                    <input
                      type="number"
                      name="chest"
                      defaultValue={profile?.chest}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Hips (cm)
                    </label>
                    <input
                      type="number"
                      name="hips"
                      defaultValue={profile?.hips}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Activity Level
                  </label>
                  <select
                    name="activity_level"
                    defaultValue={profile?.activity_level || 'medium'}
                    className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <FileUpload
                  label="Body Photo (Optional)"
                  onFileSelect={(file) => setBodyImage(file)}
                  currentFile={profile?.body_image}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Current Diet
                  </label>
                  <textarea
                    name="current_diet"
                    defaultValue={profile?.current_diet}
                    className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Allergies or Food Intolerances
                  </label>
                  <input
                    type="text"
                    name="allergies"
                    defaultValue={profile?.allergies}
                    className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                    placeholder="e.g., lactose, nuts, gluten"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Goal
                  </label>
                  <select
                    name="goal"
                    defaultValue={profile?.goal || 'lose'}
                    className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                    required
                  >
                    <option value="lose">Lose Weight</option>
                    <option value="gain">Gain Weight</option>
                    <option value="muscle">Build Muscle</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Target Areas
                  </label>
                  <input
                    type="text"
                    name="target_areas"
                    defaultValue={profile?.target_areas?.join(', ')}
                    className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                    placeholder="e.g., arms, belly, legs"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Meals per Day
                    </label>
                    <input
                      type="number"
                      name="meals_per_day"
                      min="3"
                      max="6"
                      defaultValue={profile?.meals_per_day || 3}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Favorite Foods
                    </label>
                    <input
                      type="text"
                      name="favorite_foods"
                      defaultValue={profile?.favorite_foods?.join(', ')}
                      className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                      placeholder="e.g., chicken, rice, avocado"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Blood Test Results (Optional)
                </label>
                <textarea
                  name="blood_results"
                  defaultValue={profile?.blood_results}
                  className="w-full px-4 py-2 bg-gray-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-custom-orange-200"
                  rows={3}
                  placeholder="Enter your blood test results (vitamins, minerals, etc.)"
                />
              </div>

              <FileUpload
                label="Blood Test Results Image (Optional)"
                onFileSelect={(file) => setBloodResultsImage(file)}
                currentFile={profile?.blood_results_image}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-custom-orange-400 to-custom-orange-500 text-white rounded-xl font-semibold hover:from-custom-orange-500 hover:to-custom-orange-600 transition-all shadow-lg flex items-center gap-2"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
                {loading ? 'Generating...' : 'Generate Diet Plan'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}